import { prefersReducedMotion } from '@theme/utilities';

/**
 * Clamps a number between a lower and upper bound.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * `<scroll-timeline>` — a vertical timeline whose central line fills as the section scrolls
 * through the viewport, while entries reveal on enter and their dots activate behind the fill.
 *
 * No animation library: reveal uses an IntersectionObserver, the progress fill is driven by a
 * `--timeline-progress` (0–1) custom property updated on a rAF-coalesced scroll/resize handler.
 * All motion is opt-out via `prefers-reduced-motion` (CSS) and short-circuited here too.
 */
class ScrollTimeline extends HTMLElement {
  /** Fraction of the viewport height the fill's leading edge tracks (0.5 = vertical center). */
  static #REFERENCE_RATIO = 0.5;

  #controller = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {HTMLElement[]} */
  #entries = [];

  /** @type {HTMLElement | null} */
  #rail = null;

  #ticking = false;

  connectedCallback() {
    this.#entries = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.timeline-entry')));
    this.#rail = this.querySelector('.timeline__rail');

    // Reduced motion: reveal everything, fill fully, and skip the scroll wiring entirely.
    if (prefersReducedMotion()) {
      this.style.setProperty('--timeline-progress', '1');
      for (const entry of this.#entries) entry.classList.add('is-revealed', 'is-active');
      return;
    }

    this.#observeReveal();

    const { signal } = this.#controller;
    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    window.addEventListener('resize', this.#onScroll, { passive: true, signal });

    this.#update();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
  }

  /**
   * Reveals each entry once (adds `.is-revealed`), then stops observing it.
   */
  #observeReveal() {
    this.#observer = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          if (!record.isIntersecting) continue;
          record.target.classList.add('is-revealed');
          this.#observer?.unobserve(record.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );

    for (const entry of this.#entries) this.#observer.observe(entry);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#update();
      this.#ticking = false;
    });
  };

  /**
   * Recomputes the fill progress (0–1) and per-entry `.is-active` state.
   *
   * Progress is measured against the RAIL (not the whole section) so the fill's leading edge tracks
   * the viewport's vertical midpoint as it sweeps the rail: `(reference - railTop) / railHeight`.
   * This keeps the fill in sync with the entry being read regardless of how much taller or shorter
   * the section is than the viewport (the earlier section-vs-viewport mapping raced when the section
   * was barely taller than the viewport). Dots activate exactly when their center crosses the same
   * reference line — precise even when entries have unequal heights (image vs. text-only).
   */
  #update() {
    if (!this.#rail) return;

    const railRect = this.#rail.getBoundingClientRect();
    if (railRect.height <= 0) return;

    const reference = window.innerHeight * ScrollTimeline.#REFERENCE_RATIO;
    const progress = clamp((reference - railRect.top) / railRect.height, 0, 1);
    this.style.setProperty('--timeline-progress', progress.toFixed(4));

    for (const entry of this.#entries) {
      const dot = entry.querySelector('.timeline-entry__dot');
      if (!dot) continue;
      const dotRect = dot.getBoundingClientRect();
      entry.classList.toggle('is-active', dotRect.top + dotRect.height / 2 <= reference);
    }
  }
}

if (!customElements.get('scroll-timeline')) {
  customElements.define('scroll-timeline', ScrollTimeline);
}
